import { useCallback, useEffect, useState } from 'react'
import { slowConnectionDetector } from '@/lib/slowConnectionDetector'
import { useOfflineModeStore } from '@/store/useOfflineModeStore'
import {
  SLOW_PROMPT_SNOOZE_MS,
  isSnoozed,
  readSnoozedUntil,
  writeSnoozedUntil,
} from '@/lib/slowConnectionSnooze'

type SlowConnectionState = {
  /** Whether the "switch to offline mode?" suggestion should be offered. */
  isPromptVisible: boolean
  /** User said no — snoozed for `SLOW_PROMPT_SNOOZE_MS`, across reloads. */
  dismiss: () => void
  /** User said yes. */
  switchToOfflineMode: () => void
}

/**
 * Subscribes to slow/fast connection transitions from the singleton detector
 * and decides whether the suggestion may be shown.
 *
 * - When offline mode is enabled, detection resets (the suggestion is moot).
 * - A dismissal is persisted, not held in component state. The detector's
 *   hysteresis can flip fast→slow repeatedly inside one bad-network session,
 *   and the previous session-scoped flag re-armed on every one of those
 *   flips, so saying "no" bought the user only minutes of quiet.
 */
export function useSlowConnectionDetector(): SlowConnectionState {
  const [isSlowDetected, setIsSlowDetected] = useState(() => slowConnectionDetector.isSlow())
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(() => readSnoozedUntil())
  const isOfflineModeEnabled = useOfflineModeStore(s => s.isOfflineModeEnabled)
  const enableOfflineMode = useOfflineModeStore(s => s.enableOfflineMode)

  useEffect(() => {
    const unsubSlow = slowConnectionDetector.onSlowDetected(() => setIsSlowDetected(true))
    const unsubFast = slowConnectionDetector.onFastDetected(() => setIsSlowDetected(false))

    return () => {
      unsubSlow()
      unsubFast()
    }
  }, [])

  // Reset both React state AND the singleton detector when offline mode is toggled on.
  // Without resetting the detector, its internal `slow=true` would cause the prompt
  // to silently NOT reappear if the user later disables offline mode (since the
  // detector wouldn't fire a new onSlowDetected — it already considers itself slow).
  useEffect(() => {
    if (isOfflineModeEnabled) {
      slowConnectionDetector.reset()
      setIsSlowDetected(false)
    }
  }, [isOfflineModeEnabled])

  // A snooze has to WAKE UP. `isSnoozed(Date.now(), ...)` is evaluated during
  // render, and nothing re-renders this hook when wall-clock time passes: the
  // detector only emits on a fast->slow transition, so a connection that was
  // already slow when the user dismissed would never re-arm the suggestion,
  // even days later. Two wake-ups, because neither alone is enough — a timer
  // for the foreground case, and a focus/visibility re-check for the case
  // where the device slept through the deadline and the timer was throttled.
  useEffect(() => {
    if (snoozedUntil === null) return

    const remaining = snoozedUntil - Date.now()
    if (remaining <= 0) {
      setSnoozedUntil(null)
      return
    }

    const timer = setTimeout(() => setSnoozedUntil(null), remaining)
    return () => clearTimeout(timer)
  }, [snoozedUntil])

  useEffect(() => {
    const recheck = () =>
      setSnoozedUntil(current => (isSnoozed(Date.now(), current) ? current : null))

    window.addEventListener('focus', recheck)
    document.addEventListener('visibilitychange', recheck)

    return () => {
      window.removeEventListener('focus', recheck)
      document.removeEventListener('visibilitychange', recheck)
    }
  }, [])

  const dismiss = useCallback(() => {
    const until = Date.now() + SLOW_PROMPT_SNOOZE_MS
    writeSnoozedUntil(until)
    setSnoozedUntil(until)
  }, [])

  const switchToOfflineMode = useCallback(() => {
    enableOfflineMode()
  }, [enableOfflineMode])

  const isPromptVisible =
    isSlowDetected && !isOfflineModeEnabled && !isSnoozed(Date.now(), snoozedUntil)

  return { isPromptVisible, dismiss, switchToOfflineMode }
}
